import {
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { roundTo } from "@/lib/utils/numbers";
import { useMemo } from "react";
import dayjs from "dayjs";
import { DSSATSimulation } from "@/types/dssat";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { DateValue, RangeValue, Spinner } from "@heroui/react";
import useRequest from "@/lib/request/useRequest";
import { sortArrayByKey } from "@/lib/utils/sorting";

const ROOT_DENSITY_COLORS = [
    "#8B4513",
    "#A0522D",
    "#D2691E",
    "#FF7F50",
    "#FF8C00",
    "#FFA500",
] as const;

const CHART_HEIGHT = 250;

const ROOT_DENSITY_KEYS = [
    "root_density_5cm",
    "root_density_15cm",
    "root_density_30cm",
    "root_density_60cm",
    "root_density_100cm",
    "root_density_200cm",
] as const;

const ROOT_DENSITY_LABELS: Record<(typeof ROOT_DENSITY_KEYS)[number], string> =
    {
        root_density_5cm: "chartLabels.rootDensity5cm",
        root_density_15cm: "chartLabels.rootDensity15cm",
        root_density_30cm: "chartLabels.rootDensity30cm",
        root_density_60cm: "chartLabels.rootDensity60cm",
        root_density_100cm: "chartLabels.rootDensity100cm",
        root_density_200cm: "chartLabels.rootDensity200cm",
    };

function RootDensityChart({
    dateRange,
}: {
    dateRange: RangeValue<DateValue> | null;
}) {
    const { t } = useTranslation("insights");
    const { formatDateMMDDYYYY } = useI18n();
    const { useGetRequest } = useRequest();
    const { fieldId, insightsParams } = useFieldDashboard() ?? {};

    const [startDate, endDate] = useMemo(() => {
        return [
            dayjs(dateRange?.start?.toString()),
            dayjs(dateRange?.end?.toString()),
        ];
    }, [dateRange]);

    const dssatSimulationsQuery = useGetRequest<DSSATSimulation[]>({
        url: `/fields/${fieldId}/dssat_simulations/`,
        enabled: !!fieldId,
    });

    const data = useMemo(() => {
        const cycleId = insightsParams?.crop_cycle_id;
        if (!dssatSimulationsQuery.data || cycleId == null) return [];
        return sortArrayByKey(
            dssatSimulationsQuery.data.filter((d) => {
                const date = dayjs(d.date);
                return (
                    d.crop_cycle === cycleId &&
                    (date.isSame(startDate, "day") ||
                        date.isAfter(startDate)) &&
                    (date.isSame(endDate, "day") || date.isBefore(endDate))
                );
            }),
            "date",
        ).map((row) => ({
            ...row,
            date: dayjs(row.date).toDate(),
            root_density_5cm: row.root_density_5cm
                ? Number(row.root_density_5cm)
                : undefined,
            root_density_15cm: row.root_density_15cm
                ? Number(row.root_density_15cm)
                : undefined,
            root_density_30cm: row.root_density_30cm
                ? Number(row.root_density_30cm)
                : undefined,
            root_density_60cm: row.root_density_60cm
                ? Number(row.root_density_60cm)
                : undefined,
            root_density_100cm: row.root_density_100cm
                ? Number(row.root_density_100cm)
                : undefined,
            root_density_200cm: row.root_density_200cm
                ? Number(row.root_density_200cm)
                : undefined,
        }));
    }, [
        dssatSimulationsQuery.data,
        insightsParams?.crop_cycle_id,
        startDate,
        endDate,
    ]);

    const maxRootDensity = useMemo(() => {
        const values = data.flatMap((d) =>
            ROOT_DENSITY_KEYS.map((key) => d[key]).filter(
                (v): v is number => typeof v === "number" && !Number.isNaN(v),
            ),
        );
        if (!values.length) return 1;
        return Math.max(...values) + 0.1;
    }, [data]);

    return (
        <>
            <div className="font-semibold">{t("chartLabels.rootDensity")}</div>
            {dssatSimulationsQuery.isLoading ? (
                <Spinner label={t("messages.loadingData")} />
            ) : !data.length ? (
                <div className="text-sm italic text-center mt-4">
                    {t("messages.noDataSelectedDates")}
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                    <LineChart
                        data={data}
                        margin={{ bottom: 8, left: 8, top: 15 }}
                    >
                        <XAxis
                            dataKey="date"
                            scale="time"
                            spacing={50}
                            angle={-8}
                            fontSize={12}
                            tickFormatter={(tick) => formatDateMMDDYYYY(tick)}
                        />
                        <YAxis
                            label={{
                                value: t("chartLabels.rootDensitycm-cm3"),
                                angle: -90,
                                dx: -30,
                            }}
                            fontSize={12}
                            domain={[0, maxRootDensity]}
                        />
                        <Tooltip
                            labelFormatter={(label) =>
                                formatDateMMDDYYYY(label)
                            }
                            formatter={(value, name) => [
                                roundTo(value as number, 2),
                                name,
                            ]}
                            contentStyle={{ fontSize: 12 }}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                        <Legend
                            verticalAlign="top"
                            height={36}
                            fontSize={12}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                        {ROOT_DENSITY_KEYS.map((key, i) => (
                            <Line
                                key={key}
                                dataKey={key}
                                name={t(ROOT_DENSITY_LABELS[key])}
                                stroke={ROOT_DENSITY_COLORS[i]}
                                dot={false}
                                strokeWidth={2}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            )}
        </>
    );
}

export default RootDensityChart;
